<script lang="ts" setup>
import { Loader as GMapApiLoader } from '@googlemaps/js-api-loader'
import type { Agency } from '~/types/api'
import { easeInOutQuad, tweenObjectParamsTo } from '~/utils/tween'

interface AgenciesMapProps {
    agencies: Agency[]
    selectedAgency: Agency | null
    hiddenOnMobile?: boolean
    locked?: boolean
    id: string
}

const props = withDefaults(defineProps<AgenciesMapProps>(), {
    locked: false,
})
const emits = defineEmits<{
    (e: 'onMarkerClicked', value: Agency): void
}>()
const runtimeConfig = useRuntimeConfig()

const bounds = ref<null | google.maps.LatLngBounds>(null)
const map = ref<null | google.maps.Map>(null)
const markers = ref<(unknown | google.maps.Marker)[]>([])

const mapElement = ref<HTMLElement | null>(null)

const loader = new GMapApiLoader({
    apiKey: runtimeConfig.public.googleMapsKey,
})

const selectedAgency = computed<Agency | null>(() => {
    return props.selectedAgency
})

const agenciesWithGeo = computed(() => {
    return props.agencies.filter((agency: Agency) => {
        return agency?.location?.geo?.latitude && agency?.location?.geo?.longitude
    })
})

const initMap = async () => {
    const hidden = props.hiddenOnMobile && window?.innerWidth < getBreakpointValue('md')

    if (!mapElement.value || hidden) return

    const { LatLngBounds, Size, Point } = await loader.importLibrary('core')
    const { Map } = await loader.importLibrary('maps')
    const { Marker } = await loader.importLibrary('marker')

    bounds.value = new LatLngBounds()
    markers.value = agenciesWithGeo.value.map((agency: Agency) => {
        const marker = new Marker({
            title: agency.title,
            icon: {
                url: '/maps/mingat-pin.svg',
                size: new Size(29, 41),
                anchor: new Point(14, 41),
            },
            animation: 'BOUNCE', // Animation.BOUNCE
            position: {
                lat: Number(agency.location?.geo?.latitude || '0'),
                lng: Number(agency.location?.geo?.longitude || '0'),
            },
        })

        // markers can only be keyboard focusable when they have click listeners
        // open info window when marker is clicked
        marker.addListener('click', () => {
            emits('onMarkerClicked', agency)
        })

        const pos = marker.getPosition?.() || marker.position
        if (pos) bounds.value?.extend(pos)

        return marker
    })

    const DEFAULT_BOUNDS = {
        north: 48,
        south: 42,
        west: 0,
        east: 10,
    }

    map.value = new Map(mapElement.value, {
        mapId: props.id,
        minZoom: 7,
        maxZoom: 17,
        zoomControl: !props.locked,
        scaleControl: false,
        draggable: !props.locked,
        scrollwheel: !props.locked,
        panControl: false,
        disableDoubleClickZoom: true,
        streetViewControl: false,
        disableDefaultUI: true,
        mapTypeId: 'roadmap',
        gestureHandling: 'auto',
        restriction: {
            latLngBounds: DEFAULT_BOUNDS,
            strictBounds: true,
        },
    })

    markers.value.forEach((marker) => {
        marker.setMap(map.value)
    })
    resetBounds()
}

const resetBounds = () => {
    if (map.value && bounds.value) map.value?.fitBounds(bounds.value, 20)
}

watch(mapElement, async () => {
    try {
        await initMap()
    }
    catch (e) {
        error.value = e
    }
    finally {
        ready.value = true
    }
})

watch(selectedAgency, (newAgency: Agency | null) => {
    if (newAgency) {
        const marker = markers.value.find((marker) => {
            return marker.title === newAgency.title
        })
        if (marker && map.value) {
            tweenObjectParamsTo(
                {
                    zoom: map.value?.getZoom() || 7,
                    lat: map.value?.getCenter().lat() || 0,
                    lng: map.value?.getCenter().lng() || 0,
                },
                {
                    zoom: 15,
                    lat: marker.position.lat(),
                    lng: marker.position.lng(),
                },
                2000,
                (value) => {
                    map.value?.moveCamera({
                        zoom: value.zoom,
                        center: {
                            lat: value.lat,
                            lng: value.lng,
                        },
                    })
                },
                easeInOutQuad,
            )
        }
    }
    else {
        resetBounds()
    }
})

const error = ref<unknown | undefined>()
const ready = ref(false)

const $style = useCssModule()
const rootClasses = computed(() => {
    return [$style.map,
        !map.value && $style['map--hidden'],
        error.value && $style['map--error'],
    ]
})
</script>

<template>
    <div
        ref="mapElement"
        :class="rootClasses"
        :aria-busy="ready ? 'false' : 'true'"
        :aria-live="ready ? 'polite' : undefined"
        class="loading-animation"
    >
        <VNotificationContent
            v-if="error"
            :class="$style.notification"
            icon-name="warning"
            :title="$t('an_error_occured')"
        />
    </div>
</template>

<style lang="scss" module>
.map {
    --theme-skeleton-gradient: white;
    --theme-skeleton-background: #{color(surfaces-secondary)};

    display: flex;
    min-width: min(300px, 100%);
    min-height: rem(310);
    align-items: center;
    border-radius: var(--radius-md);

    &[aria-busy="false"] {
        animation: none;
    }

    &--hidden {
        min-height: initial;
    }

    &--error {
        border: 1px solid var(--color-error);
        border-radius: rem(8);
    }

    @include media('<md') {
        pointer-events: none;
    }

    @include media('>=md') {
        min-height: 500px;
    }
}
</style>
